import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Collapse, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiHandler } from '../services/apiHandler';
import ConfirmAlert from './confirmAlert';
import ErrorAlert from './ErrorAlert';
import Modal from './Modal';
import PrimaryButton from './PrimaryButton';
import SuccessAlert from './SuccessAlert';
import './Vendor.css';
import VendorBandInfoCollapsible from './VendorBandInfoCollapsible';
import VendorGeneralInfoCollapsible from './VendorGeneralInfoCollapsible';
import VendorProductDetailsCollapsible from './VendorProductDetailsCollapsible';
import VendorRegulatoryInfoCollapsible from './VendorRegulatoryInfoCollapsible';
import VendorTransmissionInfoCollapsible from './VendorTransmissionInfoCollapsible';
import WarningAlert from './WarningAlert';

interface VendorProps {
  type: string;
  title: string;
  selectedVendorId: string | number;
  onChange: any;
  options: any[];
  refresh: any;
  processing: any;
}

export interface IdefaultVendor {
  id?: string;
  vendor_model: string;
  software_version: string;
  hardware_version: string;
  firmware_version: string;
  vendor_name: string;
  antenna_pattern: string;
  cbsd_category: string;
  fcc_id: string;
  antenna_beamwidth: number;
  antenna_model: string;
  antenna_gain: string;
  eirp_capability: string;
  radio_technology: string;
  transmitters: string;
  directionality: string;
  mounting_type: string;
  technology_type: string;
  sas_user_id: string;
  vendor_type: 'Airspan' | 'Nokia' | '';
  vertical_beamwidth?: number;
  transmission_power?: number;
  front_to_back_ratio?: number;
  feeder_loss?: number;
  bands?: BandDetails[];
  equipment_type?: string;
  child_equipment_type?: string;
  has_cable_modem?: boolean;
  product_number?: string;
  ip_rating?: string;
  backhaul?: string;
  indoor_rated?: boolean;
  regulatory_certification?: string;
  regulatory_country?: string;
}

export interface BandDetails {
  id?: string;
  band_name?: string;
  band_type?: string;
  cbrs_support?: boolean;
  afc_support?: boolean;
  cbrs_category?: string;
  six_ghz_device_category?: string;
  six_ghz_device_type?: string;
  start_frequency?: number;
  end_frequency?: number;
  bandwidth?: number;
}

const Vendor: React.FC<VendorProps> = ({ title, selectedVendorId, onChange, options, refresh, processing, type }) => {
  let navigate = useNavigate();
  const defaulVendorDetails: IdefaultVendor = {
    id: '',
    vendor_model: '',
    software_version: '',
    hardware_version: '',
    firmware_version: '',
    vendor_name: '',
    antenna_pattern: '',
    fcc_id: '',
    antenna_beamwidth: 0,
    cbsd_category: '',
    antenna_model: '',
    antenna_gain: '',
    eirp_capability: '',
    radio_technology: '',
    transmitters: '',
    directionality: '',
    mounting_type: '',
    technology_type: type || '',
    sas_user_id: '',
    vendor_type: '',
    vertical_beamwidth: 0,
    transmission_power: 0,
    front_to_back_ratio: 0,
    feeder_loss: 0,
    bands: [],
    equipment_type: '',
    child_equipment_type: '',
    has_cable_modem: false,
    product_number: '',
    ip_rating: '',
    backhaul: '',
    indoor_rated: false,
    regulatory_certification: '',
    regulatory_country: 'us',
  };

  const [modalVisible, toggleModalVisibility] = useState(false);
  const [vendorDetails, setVendorDetails] = useState<IdefaultVendor>(defaulVendorDetails);
  const [actionType, setActionType] = useState<string>('add');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorAlert, setErrorAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successAlert, setSuccessAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [warningAlert, setWarningAlert] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisibility] = useState(false);

  useEffect(() => {
    if (modalVisible === false || actionType === 'add') {
      setVendorDetails(defaulVendorDetails);
    }
  }, [modalVisible, actionType]);

  const setWarning = (message: string) => {
    setWarningMessage(message);
    setWarningAlert(true);
  };

  const validateDetails = (): boolean => {
    if (vendorDetails.vendor_name.trim() === '') {
      setWarning('Name is required');
      return false;
    } else if (vendorDetails.vendor_model.trim() === '') {
      setWarning('Model is required');
      return false;
    } else if (isNaN(Number(vendorDetails.antenna_beamwidth)) || vendorDetails.antenna_beamwidth.toString().trim() === '') {
      setWarning('Horizontal Beamwidth is required');
      return false;
    } else if (isNaN(Number(vendorDetails.vertical_beamwidth)) || vendorDetails?.vertical_beamwidth?.toString().trim() === '') {
      setWarning('Vertical Beamwidth is required');
      return false;
    } else if (Number(vendorDetails.antenna_beamwidth) < 0 || Number(vendorDetails.antenna_beamwidth) > 360) {
      setWarning('Antenna Beamwidth should be between 0 and 360');
      return false;
    } else if (Number(vendorDetails.vertical_beamwidth) < 0 || Number(vendorDetails.vertical_beamwidth) > 360) {
      setWarning('Vertical Beamwidth should be between 0 and 360');
      return false;
    } else if (vendorDetails.antenna_model.trim() === '') {
      setWarning('Antenna Model is required');
      return false;
    } else if (vendorDetails.antenna_gain.trim() === '') {
      setWarning('Antenna Gain is required');
      return false;
    } else if (vendorDetails.directionality.trim() === '') {
      setWarning('Directionality is required');
      return false;
    }
    return true;
  };

  const create = () => {
    let validated: boolean = validateDetails();
    console.log(validated);

    if (!validated) return;
    processing(true);
    let requestBody: any = vendorDetails;
    requestBody['mounting_type'] = type === 'cbrs_5g' ? title : '';
    delete requestBody.id;
    ApiHandler({ apiName: 'addVendor', body: requestBody, params: {} })
      .then((response: any) => {
        processing(false);
        setSuccessMessage(response.data.message);
        setSuccessAlert(true);
        toggleModalVisibility(false);
        refresh();
      })
      .catch((error: any) => {
        processing(false);
        if (error.doLogout === true) {
          setErrorMessage('Session Timed Out');
          setErrorAlert(true);
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setErrorMessage(error.data.message);
          setErrorAlert(true);
        }
      });
  };
  const edit = () => {
    processing(true);
    ApiHandler({
      apiName: 'getVendorDetails',
      body: {},
      params: { vendor_id: selectedVendorId },
    })
      .then((response: any) => {
        processing(false);
        setVendorDetails(response.data.vendor);
        setActionType('edit');
        toggleModalVisibility(true);
      })
      .catch((error: any) => {
        processing(false);
        if (error.doLogout === true) {
          setErrorMessage('Session Timed Out');
          setErrorAlert(true);
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setErrorMessage(error.data.message);
          setErrorAlert(true);
        }
      });
  };

  const update = () => {
    let validated: boolean = validateDetails();
    if (!validated) return;
    processing(true);
    let requestBody: any = vendorDetails;
    requestBody['mounting_type'] = type === 'cbrs_5g' ? title : '';
    requestBody['technology_type'] = type;
    delete requestBody.id;
    ApiHandler({
      apiName: 'updateVendor',
      body: requestBody,
      params: { vendor_id: selectedVendorId },
    })
      .then((response: any) => {
        processing(false);
        setSuccessMessage(response.data.message);
        setSuccessAlert(true);
        toggleModalVisibility(false);
        refresh();
      })
      .catch((error: any) => {
        processing(false);
        if (error.doLogout === true) {
          setErrorMessage('Session Timed Out');
          setErrorAlert(true);
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setErrorMessage(error.data.message);
          setErrorAlert(true);
        }
      });
  };
  const remove = (e: any) => {
    e.preventDefault();
    setDeleteConfirmVisibility(false);
    processing(true);
    ApiHandler({
      apiName: 'deleteVendor',
      body: {},
      params: { vendor_id: selectedVendorId },
    })
      .then((response: any) => {
        processing(false);
        setSuccessMessage(response.data.message);
        setSuccessAlert(true);
        toggleModalVisibility(false);
        refresh();
      })
      .catch((error: any) => {
        processing(false);
        if (error.doLogout === true) {
          setErrorMessage('Session Timed Out');
          setErrorAlert(true);
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setErrorMessage(error.data.message);
          setErrorAlert(true);
        }
      });
  };

  const handleDetailsChange = (key: string, value: any, section: 'general' | 'product' | 'band' | 'regulatory' | 'transmission') => {
    if (section === 'band') {
      setVendorDetails((prevValues: any) => {
        return { ...prevValues, bands: [...prevValues.bands, value] };
      });
      if (key === 'cbrs_category') {
        setVendorDetails((prevValues: any) => {
          return { ...prevValues, cbsd_category: value };
        });
      }
    } else {
      setVendorDetails((prevValues: any) => {
        return { ...prevValues, [key]: value };
      });
    }
  };

  return (
    <div>
      <div className="col-wise mt-2">
        <div className="row-wise justify-content-start vertical-center">
          <span className="sub-heading-vendors">{title == 'Aerial' ? 'Strand' : title == 'ROE' ? 'Macro' : title}</span>

          <span
            className="add-span"
            onClick={(e: any) => {
              e.preventDefault();
              setActionType('add');
              toggleModalVisibility(!modalVisible);
            }}>
            ADD
            <span className="material-icons-round font-14">add</span>
          </span>
        </div>
        <div className="row-wise w-100 vertical-center">
          <div className={`dropdown w-100`}>
            <button className="w-100" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              {options?.filter((option: any) => option?.id?.toString() === selectedVendorId.toString()).length > 0
                ? `${options?.filter((option: any) => option?.id?.toString() === selectedVendorId.toString())[0].vendor_name}-${
                    options?.filter((option: any) => option?.id?.toString() === selectedVendorId.toString())[0].vendor_model
                  }`
                : 'Select ' + (title == 'Aerial' ? 'Strand' : title == 'ROE' ? 'Macro' : title) + ' Vendor'}
            </button>

            <ul className={`dropdown-menu w-100`}>
              {options &&
                options.length > 0 &&
                options.map((option: any) => {
                  return (
                    <li key={option.id}>
                      <span
                        onClick={() => {
                          onChange(option.id);
                        }}
                        className="dropdown-item">
                        {`${option.vendor_name}-${option.vendor_model}`}
                      </span>
                    </li>
                  );
                })}
            </ul>
          </div>
          <Space className="vertical-center justify-content-evenly ms-2">
            <EditOutlined onClick={edit} style={{ color: '#1677ff' }} />
            <DeleteOutlined
              onClick={() => {
                setDeleteConfirmVisibility(true);
              }}
              style={{ color: '#d11a2a' }}
            />
          </Space>
        </div>
      </div>

      {modalVisible ? (
        <Modal show={modalVisible}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content app-card border-0 p-0">
              <div className="modal-header">
                <Typography.Title level={5}>
                  {actionType.charAt(0).toUpperCase() + actionType.slice(1)} {title == 'Aerial' ? 'Strand' : title == 'ROE' ? 'Macro' : title} Vendor
                </Typography.Title>
                <button
                  type="button"
                  className="btn-close"
                  onClick={(e: any) => {
                    e.preventDefault();
                    toggleModalVisibility(!modalVisible);
                  }}></button>
              </div>
              <div className="modal-body overflow-y-auto">
                <Collapse
                  accordion
                  defaultActiveKey={['1']}
                  expandIconPosition="end"
                  size="large"
                  items={[
                    {
                      key: '1',
                      label: <Typography.Title level={5}>General Information</Typography.Title>,
                      children: (
                        <VendorGeneralInfoCollapsible
                          vendorDetails={vendorDetails}
                          onChange={(key: string, value: any) => handleDetailsChange(key, value, 'general')}
                        />
                      ),
                    },
                    {
                      key: '2',
                      label: <Typography.Title level={5}>Product Details</Typography.Title>,
                      children: (
                        <VendorProductDetailsCollapsible
                          vendorDetails={vendorDetails}
                          onChange={(key: string, value: any) => handleDetailsChange(key, value, 'product')}
                        />
                      ),
                    },
                    {
                      key: '3',
                      label: <Typography.Title level={5}>Band Information</Typography.Title>,
                      children: (
                        <VendorBandInfoCollapsible
                          vendorDetails={vendorDetails}
                          onAddBand={(bandDetails: BandDetails) => handleDetailsChange('bands', bandDetails, 'band')}
                          onDeleteBand={(index: number) =>
                            setVendorDetails((prevValues: any) => {
                              return { ...prevValues, bands: prevValues.bands.filter((band: BandDetails, i: number) => i !== index) };
                            })
                          }
                        />
                      ),
                    },
                    {
                      key: '4',
                      label: <Typography.Title level={5}>Regulatory Information</Typography.Title>,
                      children: (
                        <VendorRegulatoryInfoCollapsible
                          vendorDetails={vendorDetails}
                          onChange={(key: string, value: any) => handleDetailsChange(key, value, 'regulatory')}
                        />
                      ),
                    },
                    {
                      key: '5',
                      label: <Typography.Title level={5}>Transmission Information</Typography.Title>,
                      children: (
                        <VendorTransmissionInfoCollapsible
                          vendorDetails={vendorDetails}
                          onChange={(key: string, value: any) => handleDetailsChange(key, value, 'transmission')}
                        />
                      ),
                    },
                  ]}
                />
              </div>
              <div className="modal-footer">
                <PrimaryButton label={actionType === 'add' ? 'Create' : 'Update'} disable={false} clicked={actionType === 'add' ? create : update} />
              </div>
            </div>
          </div>
        </Modal>
      ) : null}

      <ErrorAlert
        show={errorAlert}
        onDismiss={() => {
          setErrorAlert(false);
        }}
        message={errorMessage}
      />
      <SuccessAlert
        show={successAlert}
        onDismiss={() => {
          setSuccessAlert(false);
        }}
        message={successMessage}
      />
      <WarningAlert
        show={warningAlert}
        onDismiss={() => {
          setWarningAlert(false);
        }}
        message={warningMessage}
      />
      <ConfirmAlert
        title={`Confirm Delete`}
        message="Are you sure you want to delete the vendor?"
        primaryText="Confirm"
        secondaryText="Cancel"
        secondaryAction={() => {
          setDeleteConfirmVisibility(false);
        }}
        primaryAction={remove}
        visible={deleteConfirmVisible}
        onClose={() => {
          setDeleteConfirmVisibility(false);
        }}
      />
    </div>
  );
};

export default Vendor;
